import { useEffect, useId, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { AuthStyles } from './styles.js'
import { ACCOUNT_HINTS, type AccountHint } from './accounts.js'
import { SignInError } from './session.js'

/**
 * The sign-in form.
 *
 * A real form, not a PIN pad: it is the same screen on the phone, the desktop
 * planner and the wall tablet, and it is the one place in the product where
 * getting the details wrong is expensive.
 *
 *  - **Labels are real `<label for>`**, not placeholders. A placeholder
 *    disappears the moment you type, which on a small screen is exactly when
 *    somebody needs to know which field they are in.
 *  - **`autocomplete` is set correctly**, so a password manager fills both
 *    fields. `username` rather than `email` is deliberate: it is what managers
 *    pair with `current-password`.
 *  - **The error is announced and referenced.** `role="alert"` speaks it, and
 *    `aria-describedby` on both inputs ties it to the fields it is about, so a
 *    screen-reader user who tabs back into the form hears why it failed.
 *  - **Enter submits**, because it is a `<form>` with a submit button and not a
 *    div with a click handler.
 *  - **`noValidate`** hands validation to us. The browser's own bubble cannot be
 *    styled, vanishes on the next tap, and is not what `aria-describedby`
 *    points at.
 */
export interface SignInProps {
  /**
   * Performs the sign-in. Rejecting with a `SignInError` puts its message on
   * screen verbatim, which is why those messages are written for people.
   */
  onSignIn: (email: string, password: string) => Promise<unknown>
  /** Prefills the address, e.g. the one this device used last. */
  initialEmail?: string
  /** The addresses offered as one-tap hints. Defaults to the four accounts. */
  hints?: readonly AccountHint[]
}

const messageFor = (e: unknown): string => {
  if (e instanceof SignInError) return e.message
  if (e instanceof Error && e.message !== '') return e.message
  return 'Something went wrong signing in. Try again.'
}

export function SignIn({ onSignIn, initialEmail = '', hints = ACCOUNT_HINTS }: SignInProps) {
  const [email, setEmail] = useState(initialEmail)
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  /**
   * Counts failures rather than tracking the message.
   *
   * Two identical failures in a row — pressing Sign in twice on an empty form —
   * leave `error` byte-for-byte unchanged, so a screen reader is told nothing
   * the second time and focus never moves. The count changes every time, which
   * both re-keys the alert (announcing it again) and re-runs the focus effect.
   */
  const [failures, setFailures] = useState(0)
  const [busy, setBusy] = useState(false)

  const fail = (message: string): void => {
    setError(message)
    setFailures(n => n + 1)
  }

  const baseId = useId()
  const emailId = `${baseId}-email`
  const passwordId = `${baseId}-password`
  const errorId = `${baseId}-error`
  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  // A successful sign-in unmounts this form; the state updates that follow it
  // would be writes to a component that no longer exists.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  const submit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    if (busy) return
    if (email.trim() === '' || password === '') {
      fail('Enter your email and password.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onSignIn(email.trim(), password)
      if (alive.current) setPassword('')
    } catch (err) {
      if (!alive.current) return
      fail(messageFor(err))
      // Keep the address, drop the password: the address is almost always right
      // and retyping it on a tablet keyboard is the annoying half.
      setPassword('')
    } finally {
      if (alive.current) setBusy(false)
    }
  }

  /**
   * Focus follows the error — but only once the fields are enabled again.
   * Focusing from inside the handler does nothing at all, because the inputs are
   * still disabled at that point and a disabled input cannot take focus.
   *
   * The empty field is read off the DOM rather than off state, so this hook
   * depends on the error alone and cannot fire on an unrelated keystroke.
   */
  useEffect(() => {
    if (failures === 0) return
    const target = emailRef.current?.value.trim() === '' ? emailRef.current : passwordRef.current
    target?.focus()
  }, [failures])

  const applyHint = (hint: AccountHint): void => {
    setEmail(hint.email)
    setError(null)
    passwordRef.current?.focus()
  }

  const describedBy = error !== null ? errorId : undefined

  return (
    // A landmark, not a div: this screen is the entire page while it is up, and
    // a screen-reader user arriving on it should be able to jump straight to it.
    <main className="au">
      <AuthStyles />
      <div className="au-card">
        <header className="au-head">
          <h1 className="au-title">Office OS</h1>
          <p className="au-sub">Sign in to the office.</p>
        </header>

        <form className="au-form" onSubmit={e => void submit(e)} noValidate aria-busy={busy}>
          <div className="au-field">
            <label className="au-label" htmlFor={emailId}>Email</label>
            <input
              id={emailId}
              ref={emailRef}
              className="au-input"
              type="email"
              name="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              disabled={busy}
              autoFocus={initialEmail === ''}
              {...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {})}
              aria-invalid={error !== null}
            />
          </div>

          <div className="au-field">
            <label className="au-label" htmlFor={passwordId}>Password</label>
            <input
              id={passwordId}
              ref={passwordRef}
              className="au-input"
              type="password"
              name="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={busy}
              autoFocus={initialEmail !== ''}
              {...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {})}
              aria-invalid={error !== null}
            />
          </div>

          {error !== null && (
            <p className="au-error" key={failures} id={errorId} role="alert">
              <span aria-hidden>⚠</span>
              <span>{error}</span>
            </p>
          )}

          <button className="au-submit" type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {hints.length > 0 && (
          <div className="au-hints">
            <p className="au-hints-title" id={`${baseId}-hints`}>Accounts</p>
            <div className="au-hint-row" role="group" aria-labelledby={`${baseId}-hints`}>
              {hints.map(h => (
                <button
                  key={h.email}
                  type="button"
                  className="au-hint"
                  disabled={busy}
                  onClick={() => applyHint(h)}
                  aria-label={`Use ${h.email}`}
                >
                  <span>{h.label}</span>
                  {h.note !== undefined && <span className="au-hint-note">{h.note}</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="au-foot">The office keeps working offline once you are in.</p>
      </div>
    </main>
  )
}
